import { falTruckFast } from 'shared/@generated/icons/falTruckFast';
import { useTranslation } from 'react-i18next';
import { Product } from 'shared/@generated/models';
import { Image } from 'shared/components/image';
import { Link } from 'shared/components/link';
import LikeButton from 'shared/components/like-button';
import { Text } from 'shared/components/text';
import { useRoute } from 'shared/library/routes/route';
import { useFormatter } from 'shared/library/use-formatter';
import { getProductImage } from 'shared/library/get-model-image';
import { Platform, ScrollView, View } from 'react-native';
import { useMemo } from 'react';
import { Icon } from 'shared/components/icon';
import { ShopProductRating } from 'shared/components/shop/product-rating';
import { clsx } from 'clsx';

export function ShopProductCard({ product }: { product: Product }) {
  const { t } = useTranslation('components');
  const { formatCurrency } = useFormatter();
  const route = useRoute();
  const productVariants = product.product_variants ?? [];

  const hasAnyVariantInStock = useMemo(() => {
    return productVariants.some((variant) => variant.in_stock > 0);
  }, [productVariants]);

  const variantsWithNames = useMemo(() => {
    return productVariants.filter((variant) => variant.name);
  }, [productVariants]);

  const cheapestPrice = useMemo(() => {
    return productVariants.length
      ? productVariants.reduce(
          (cheapest, current) =>
            current.pricing.local.total.amount < cheapest.pricing.local.total.amount
              ? current
              : cheapest,
          productVariants[0]!,
        )
      : 0;
  }, [productVariants]);

  return (
    <View className={Platform.OS === 'web' ? 'h-full' : undefined}>
      <View className={'absolute left-0 top-0 z-10 p-2'}>
        <LikeButton modelId={product.id} modelType={'App\\Models\\Product'} />
      </View>

      <Link
        href={route('shop.product', { product: product.slug })}
        asBlock={true}
        containerClassName={clsx(
          'border-brand-brown-dark/15 group rounded border bg-white',
          Platform.OS === 'web' && 'h-full',
        )}
      >
        <Image
          src={getProductImage(product)}
          alt={product.name}
          containerClassName={'relative aspect-square w-full'}
          imageClassName={'bg-brand-cream rounded-t object-cover group-hover:opacity-75'}
          sizeType={'card'}
          aspectRatio={1}
        />

        <View className={clsx('p-3', Platform.OS === 'web' && 'flex-1')}>
          <Text className={'text-brand-brown-dark/70 text-2xs uppercase tracking-wider md:text-xs'}>
            {product.brand.name}
          </Text>

          <Text className={'text-brand-brown-dark font-semibold group-hover:underline'}>
            {product.name}
          </Text>

          <ShopProductRating product={product} size={'sm'} className={'mt-1.5'} />

          {variantsWithNames.length > 0 && (
            <View>
              <ScrollView
                className={'mt-1.5'}
                horizontal={true}
                showsHorizontalScrollIndicator={false}
              >
                {variantsWithNames.map((variant) => (
                  <Text
                    key={variant.id}
                    className={
                      'text-brand-brown-dark/70 border-brand-brown-dark/15 mr-1.5 border px-1.5 py-0.5 text-xs'
                    }
                  >
                    {variant.name}
                  </Text>
                ))}
              </ScrollView>
            </View>
          )}

          {Platform.OS === 'web' && <View className={'flex-1'} />}

          <View className={'mt-3 gap-1.5 md:mt-6 md:flex-row md:items-center'}>
            {cheapestPrice && (
              <Text className={'text-brand-brown-dark grow font-black'}>
                {t('shop.product_card.price', {
                  price: formatCurrency(cheapestPrice.pricing.local.total.amount, undefined, 2),
                })}
              </Text>
            )}

            {hasAnyVariantInStock && (
              <View className={'flex-row items-center gap-1.5'}>
                <Icon icon={falTruckFast} className={'size-3 text-red-900'} />
                <Text className={'text-xs text-red-900'}>
                  {t('shop.product_card.fast_shipping')}
                </Text>
              </View>
            )}
          </View>
        </View>
      </Link>
    </View>
  );
}
